import { COMPLIANCE_STATUS_LABELS, type ComplianceStatus } from "@gb/contracts";
import { cn } from "@/lib/utils";

// `ComplianceStatus` was re-declared here as a local string union, and
// `crew-avatar.tsx` imported it *from this file* rather than from the contract
// — so a component tree several files deep was typed against a copy of the
// vocabulary that nothing kept in step. It comes from `@gb/contracts` now, and
// is re-exported only so existing importers keep working.
export type { ComplianceStatus };

/**
 * The design derives the pill border from its foreground at 40% opacity
 * (`border: 1.5px solid ${fg}66`), so each variant below is a foreground/tint
 * pair plus that same colour at /40.
 *
 * Colour only. The words are the contract's — this pill sits beside the thing
 * it describes, so it takes the short rendering; the CSV export, which has no
 * such context, takes `COMPLIANCE_STATUS_LABELS_STANDALONE`.
 */
const STATUS: Record<ComplianceStatus, { className: string; dot: string }> = {
  valid: {
    className: "bg-ok-bg text-ok border-ok/40",
    dot: "bg-ok",
  },
  expiring: {
    className: "bg-warn-bg text-warn border-warn/40",
    dot: "bg-warn",
  },
  expired: {
    className: "bg-danger-bg text-danger border-danger/40",
    dot: "bg-danger",
  },
  missing: {
    className: "bg-danger-bg text-danger border-danger/40",
    dot: "bg-danger",
  },
};

export function statusLabel(status: ComplianceStatus): string {
  return COMPLIANCE_STATUS_LABELS[status];
}

export function StatusPill({
  status,
  label,
  showDot = true,
  className,
}: {
  status: ComplianceStatus;
  /** Override the default label — the compliance banner uses "Due soon". */
  label?: string;
  showDot?: boolean;
  className?: string;
}) {
  const s = STATUS[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] px-[11px] py-1",
        "font-display text-[0.74rem] font-semibold uppercase tracking-[0.03em]",
        s.className,
        className,
      )}
    >
      {showDot && (
        <span className={cn("size-[9px] shrink-0 rounded-full", s.dot)} />
      )}
      {label ?? COMPLIANCE_STATUS_LABELS[status]}
    </span>
  );
}
